import { useEffect, useMemo, useState } from 'react';
import { Search, X, Plus, Pencil, Trash2, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { Button, Input, Card, CardContent, Dialog } from '../ui';
import { handleAuthError } from '../../../funciones/authHelper';

/**
 * CRUD de una entidad de "solo nombre" (Áreas, Convocatorias) en estilo DS v2
 * con acento NARANJA propio de Cursos SEPE.
 *
 * El naranja se aplica como override de los tokens de marca del DS sobre el
 * subárbol (style con --brand-*). Así Button y demás componentes del DS que
 * usan hsl(var(--brand-*)) se tiñen de naranja sin tocar el DS.
 */
const SEPE_THEME = {
  // naranja corporativo del grupo SEPE
  '--brand-dark': '21 90% 42%',   // ~#c2410c
  '--brand-light': '25 95% 53%',  // ~#f97316
};

/**
 * columnasExtra (opcional): columnas añadidas entre "Nombre" y "Acciones".
 *   [{ key, header, sortable?, render?: (item) => ReactNode }]
 *   - render da control total del contenido de la celda al padre.
 *   - sortable: header clickable con orden client-side (null → asc → desc → null).
 * Si no se pasa, la tabla se comporta exactamente como antes (retrocompatible).
 */
export default function CrudNombre({ titulo, subtitulo, placeholder, art, api, columnasExtra = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [drawer, setDrawer] = useState(null);   // { id?, nombre }
  const [saving, setSaving] = useState(false);
  const [errForm, setErrForm] = useState('');
  const [delItem, setDelItem] = useState(null);
  const [errDel, setErrDel] = useState('');
  const [orden, setOrden] = useState({ key: null, dir: null }); // dir: 'asc' | 'desc' | null

  const totalColumnas = 2 + columnasExtra.length;

  // Orden client-side por una columna extra numérica: null → asc → desc → null.
  const toggleOrden = (key) => setOrden((o) => {
    if (o.key !== key) return { key, dir: 'asc' };
    if (o.dir === 'asc') return { key, dir: 'desc' };
    return { key: null, dir: null };
  });

  const cargar = async () => {
    setLoading(true);
    try { const { data } = await api.list(); setItems(data); }
    catch (e) { if (handleAuthError) handleAuthError(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? items.filter((x) => x.nombre.toLowerCase().includes(t)) : items;
  }, [items, q]);

  const ordenados = useMemo(() => {
    if (!orden.key || !orden.dir) return filtrados;
    return [...filtrados].sort((a, b) => {
      const diff = (Number(a[orden.key]) || 0) - (Number(b[orden.key]) || 0);
      return orden.dir === 'asc' ? diff : -diff;
    });
  }, [filtrados, orden]);

  const abrirNuevo = () => { setErrForm(''); setDrawer({ nombre: '' }); };
  const abrirEdit = (x) => { setErrForm(''); setDrawer({ id: x.id, nombre: x.nombre }); };

  const guardar = async () => {
    if (!drawer.nombre.trim()) { setErrForm('El nombre es obligatorio.'); return; }
    setSaving(true); setErrForm('');
    try {
      if (drawer.id) await api.update(drawer.id, drawer);
      else await api.create(drawer);
      setDrawer(null); await cargar();
    } catch (e) {
      setErrForm(e?.response?.data?.error || 'No se pudo guardar.');
      if (handleAuthError) handleAuthError(e);
    } finally { setSaving(false); }
  };

  const confirmarBorrado = async () => {
    setErrDel('');
    try { await api.remove(delItem.id); setDelItem(null); await cargar(); }
    catch (e) {
      setErrDel(e?.response?.data?.error || 'No se pudo eliminar.');
      if (handleAuthError) handleAuthError(e);
    }
  };

  return (
    <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full" style={SEPE_THEME}>

      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">{titulo}</h1>
          <p className="text-sm text-slate-500 mt-1">{subtitulo} · <span className="font-medium text-slate-700">{items.length}</span></p>
        </div>
        <Button onClick={abrirNuevo}><Plus className="w-4 h-4 mr-1.5" /> Añadir</Button>
      </div>

      <Card className="mb-3">
        <CardContent className="p-4">
          <div className="max-w-sm">
            <Input
              prefix={<Search className="w-4 h-4" />}
              suffix={q ? <button onClick={() => setQ('')}><X className="w-4 h-4" /></button> : null}
              placeholder={placeholder}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide text-left">
              <tr>
                <th className="px-4 py-2.5">Nombre</th>
                {columnasExtra.map((cxt) => (
                  <th key={cxt.key} className="px-4 py-2.5 w-28">
                    {cxt.sortable ? (
                      <button type="button" onClick={() => toggleOrden(cxt.key)} className="inline-flex items-center gap-1 uppercase select-none hover:text-slate-700">
                        {cxt.header}
                        {orden.key === cxt.key && orden.dir === 'asc'
                          ? <ChevronUp className="w-3 h-3" />
                          : orden.key === cxt.key && orden.dir === 'desc'
                            ? <ChevronDown className="w-3 h-3" />
                            : <ChevronsUpDown className="w-3 h-3 opacity-40" />}
                      </button>
                    ) : cxt.header}
                  </th>
                ))}
                <th className="px-4 py-2.5 w-24 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={totalColumnas} className="px-4 py-10 text-center text-slate-400">Cargando…</td></tr>}
              {!loading && ordenados.length === 0 && (
                <tr><td colSpan={totalColumnas} className="px-4 py-10 text-center text-slate-400">{q ? 'Sin resultados.' : 'Aún no hay registros. Añade el primero.'}</td></tr>
              )}
              {!loading && ordenados.map((x) => (
                <tr key={x.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => abrirEdit(x)}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{x.nombre}</td>
                  {columnasExtra.map((cxt) => (
                    <td key={cxt.key} className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      {cxt.render ? cxt.render(x) : x[cxt.key]}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <button className="text-slate-400 hover:text-slate-700 mr-2" title="Editar" onClick={() => abrirEdit(x)}><Pencil className="w-4 h-4" /></button>
                    <button className="text-slate-400 hover:text-rose-500" title="Eliminar" onClick={() => { setErrDel(''); setDelItem(x); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Drawer alta/edición */}
      {drawer && (
        <>
          <div className="fixed inset-0 bg-black/25 z-40" onClick={() => setDrawer(null)} />
          <aside className="fixed top-0 right-0 h-full w-[480px] max-w-[90vw] bg-white shadow-2xl z-50 flex flex-col" style={SEPE_THEME}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">{drawer.id ? `Editar ${art}` : `Nuevo ${art}`}</h2>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setDrawer(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4 flex-1">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                <Input
                  autoFocus
                  value={drawer.nombre}
                  onChange={(e) => setDrawer({ ...drawer, nombre: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') guardar(); }}
                  placeholder={placeholder}
                />
              </div>
              {errForm && <p className="text-sm text-rose-600">{errForm}</p>}
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDrawer(null)}>Cancelar</Button>
              <Button onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
            </div>
          </aside>
        </>
      )}

      <Dialog
        open={!!delItem}
        onClose={() => setDelItem(null)}
        title={`Eliminar ${art}`}
        description={delItem ? `¿Seguro que quieres eliminar "${delItem.nombre}"?` : ''}
        footer={
          <div className="flex flex-col items-stretch gap-2">
            {errDel && <p className="text-sm text-rose-600">{errDel}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDelItem(null)}>Cancelar</Button>
              <Button variant="danger" onClick={confirmarBorrado}>Eliminar</Button>
            </div>
          </div>
        }
      />
    </div>
  );
}
